import {IAdTag} from "@pages/Root/services/api/ads/ad-model-definitions.ts";
import {IAdMapData, IAdParameter, IGeneralSize} from "@pages/Root/pages/HomeRoot/interfaces.ts";
import { DTCMBridge, BridgeEvent } from '../bridge/client-bridge-defs.ts';
import {MonetizationBridgeEvent} from "@moment/srv/utils/srv-types.ts";

export const emitAdImpressionEvent = (bridge: DTCMBridge) => {
    bridge.onEvent(BridgeEvent.ANALYTICS_EVENT, {
        eventName: "WEB_AD_IMPRESSION",
        eventPayload: {
            eventData: new Date().toISOString()
        }
    });
};

export const emitAdMonetizationEvent = (bridge: DTCMBridge, payload: MonetizationBridgeEvent) => {
    bridge.onEvent(BridgeEvent.ANALYTICS_EVENT, {
        eventName: "MONETIZATION",
        eventPayload: {
            ...payload,
            eventData: new Date().toISOString()
        }
    });
};

export const refreshAdd = (adUnitId: string) => {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(async function () {
        const slot = window.gptadslots ? window.gptadslots[`ad_unit_${adUnitId}`] : {};
        window.googletag.display(`ad_unit_${adUnitId}`);

        if (slot) {
            if (
                typeof window.pubwise !== 'undefined' &&
                window.pubwise.enabled === true
            ) {
                window.pbjs.que.push(function () {
                    slot.pwUseAdunitName = 1;
                    window.pwRegisterLazyLoad(slot, 2, [50, 0, 50, 0], 0, 768, 2);
                });
            } else {
                window.googletag.pubads().refresh([slot]);
            }
        }
    });
};

export type AdEventListener = { eventType: string, eventListener: (any)=> any }

// Store references to event listeners so they can be removed later
const activeEventListeners = new Map<string, ((event: any) => void)[]>();

export const removeAdEventListeners = (eventType: string) => {
    const listeners = activeEventListeners.get(eventType);
    if (listeners && listeners.length > 0) {
        window.googletag = window.googletag || { cmd: [] };
        window.googletag.cmd.push(function () {
            const pubads = window.googletag.pubads();
            listeners.forEach((listener) => {
                console.debug(`Removing existing ad listener for ${eventType}`);
                pubads.removeEventListener(eventType, listener);
            });
        });
        // Clear the stored listeners
        activeEventListeners.set(eventType, []);
    }
};

export const addAdEventListener = (eventType: string, eventListener: (event: any) => void) => {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(function () {
        const pubads = window.googletag.pubads();
        const existingListeners = activeEventListeners.get(eventType) || [];

        // Check if this exact listener function is already registered
        // Using a more reliable way to compare functions
        const listenerExists = existingListeners.some((existing) => {
            // Compare function bodies and names for better matching
            return existing.toString() === eventListener.toString() &&
                   existing.name === eventListener.name;
        });

        if (!listenerExists) {
            console.debug(`Adding ad listener for ${eventType}`);
            pubads.addEventListener(eventType, eventListener);

            // Store the listener reference
            existingListeners.push(eventListener);
            activeEventListeners.set(eventType, existingListeners);
        } else {
            console.debug(`Skipping duplicate ad listener for ${eventType}`);
        }
    });
};

export const initAds = (parameterList: IAdParameter[], adEventListeners?: AdEventListener[]) => {
    window.googletag = window.googletag || { cmd: [] };
    window.googletag.cmd.push(function () {
        // this will expand all ads by default but collapse if unfilled
        window.googletag.pubads().collapseEmptyDivs();

        parameterList.forEach(({ parameter, parameterValue }) => {
            window.googletag.pubads().setTargeting(parameter, parameterValue);
        });

        for (const adEventListener of adEventListeners || []) {
            addAdEventListener(adEventListener.eventType, adEventListener.eventListener);
        }

        window.googletag.pubads().disableInitialLoad();
        window.googletag.pubads().enableSingleRequest();
        window.googletag.enableServices();
    });
}

export const defineAdSlot = (
    adDataResult: IAdTag,
    adUnitId: string,
    size: IGeneralSize,
    responsiveSizeList?: IAdMapData[],
    extraTargeting?: Record<string, string | string[]>
) => {
    if (window.gptadslots && !window.gptadslots[`ad_unit_${adUnitId}`]) {
        window.googletag.cmd.push(function () {
            const newSlot = window.googletag.defineSlot(
                adDataResult?.unitPath,
                size,
                `ad_unit_${adUnitId}`
            );
            // collapse the ad div if not ads available for slot
            newSlot.setCollapseEmptyDiv();

            if (responsiveSizeList?.length) {
                const preMapping = googletag.sizeMapping();

                responsiveSizeList.forEach(({ adSizeList, screenSize }) => {
                    preMapping.addSize(screenSize, adSizeList);
                });

                const mapping = preMapping.build();
                if (newSlot && mapping) {
                    newSlot.defineSizeMapping(mapping);
                }
            }

            newSlot
                .setForceSafeFrame(true)
                .addService(window.googletag.pubads());

            if (extraTargeting) {
                for (const key in extraTargeting) {
                    newSlot.setTargeting(key, extraTargeting[key]);
                }
            }

            window.gptadslots[`ad_unit_${adUnitId}`] = newSlot;
            if (newSlot) {
                newSlot.setForceSafeFrame(true).addService(window.googletag.pubads());
            }
        });
    }

    refreshAdd(adUnitId);
};

export const cleanUpAds = () => {
    for (const slot in window.gptadslots) {
        if (window.gptadslots[slot]) {
            console.debug(`Removing ad slot ${slot}`)
            window.googletag.destroySlots([window.gptadslots[slot]]);
            delete window.gptadslots[slot];
        }
    }
}
export const DEFAULT_MOMENT_UTM_SOURCE = 'mp-moment';
